/**
 * Динамика за период: одна серия, площадь под линией.
 *
 * Одна серия — легенда не нужна, её называет заголовок. Точки подписываются
 * выборочно (максимум и последняя), а не все подряд: число на каждой точке
 * превращает график в таблицу с лишними шагами.
 *
 * Наведение обязательно: HTML-график интерактивен по своей природе.
 * Перекрестие и подсказка идут по всей высоте области, а не по попаданию
 * в саму точку — цель размером в 8px недостижима мышью.
 *
 * Ширина — по контейнеру (docs/08-mobile-roadmap.md, M4): одна единица
 * координат равна пикселю, поэтому подписи на телефоне того же размера,
 * что и на десктопе, а не ужатые вместе с картинкой. Число подписей оси
 * считается от ширины.
 */

import { useLayoutEffect, useMemo, useRef, useState } from "react";

import { Button } from "../Button";

export interface TrendPoint {
  /** Готовая подпись оси: формат дат — дело сервера, не графика. */
  label: string;
  value: number;
}

/** Ширина до первого замера и в среде без раскладки (jsdom). */
const DEFAULT_WIDTH = 640;
/** Высота на узком экране; шире 640 px график растёт в тех же пропорциях,
 *  что и прежде, когда он масштабировался целиком. */
const MIN_HEIGHT = 180;
const ASPECT = MIN_HEIGHT / DEFAULT_WIDTH;
/** Сколько места нужно одной подписи оси «дд.мм» с зазором. */
const LABEL_SPACE = 56;
const PAD = { top: 16, right: 12, bottom: 26, left: 40 };

/**
 * Верх шкалы: «круглое» число, половина которого — тоже целое, иначе
 * средняя подпись округлялась бы и повторяла соседнюю («1, 1, 0» при
 * максимуме 1). До 4 — ближайшее чётное, дальше — кратное 10.
 */
function axisCeiling(max: number): number {
  if (max <= 0) return 2;
  if (max <= 4) return Math.ceil(max / 2) * 2;
  return Math.ceil(max / 10) * 10;
}

export function TrendChart({
  title,
  subtitle,
  points,
  unit = "",
  emptyText,
}: {
  title: string;
  /** Что именно откладывается по оси — метрика и шаг. */
  subtitle?: string;
  points: TrendPoint[];
  unit?: string;
  /** Заглушка, когда все значения нулевые: пустая линия по нулю ничего не говорит. */
  emptyText?: string;
}) {
  const [asTable, setAsTable] = useState(false);
  const [hover, setHover] = useState<number | null>(null);
  const box = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(DEFAULT_WIDTH);
  const height = Math.max(MIN_HEIGHT, Math.round(width * ASPECT));

  useLayoutEffect(() => {
    const node = box.current;
    if (!node) return;
    const measure = () => {
      if (node.clientWidth > 0) setWidth(node.clientWidth);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [asTable]);

  const geometry = useMemo(() => {
    if (points.length === 0) return null;
    const max = Math.max(...points.map((p) => p.value));
    // Верхняя граница округляется вверх: подпись оси обязана называть
    // значение, которого график достигает.
    const ceiling = axisCeiling(max);
    const plotW = width - PAD.left - PAD.right;
    const plotH = height - PAD.top - PAD.bottom;

    const x = (index: number) =>
      points.length === 1
        ? PAD.left + plotW / 2
        : PAD.left + (index / (points.length - 1)) * plotW;
    const y = (value: number) => PAD.top + plotH - (value / ceiling) * plotH;

    const line = points.map((p, i) => `${String(x(i))},${String(y(p.value))}`).join(" ");
    const area = `${String(PAD.left)},${String(PAD.top + plotH)} ${line} ${String(
      x(points.length - 1),
    )},${String(PAD.top + plotH)}`;

    const maxIndex = points.reduce(
      (best, p, i) => (p.value > (points[best]?.value ?? -1) ? i : best),
      0,
    );

    return { ceiling, x, y, line, area, plotH, maxIndex };
  }, [points, width, height]);

  return (
    <figure
      // Имя задаётся явно: figcaption содержит и заголовок, и кнопку
      // переключения вида, и доступное имя из него получилось бы
      // «Название Таблицей».
      aria-label={title}
      className="viz m-0 flex flex-col gap-2.5 rounded-lg border border-line bg-surface p-4"
    >
      <figcaption className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-[13px] font-extrabold text-fg">{title}</h3>
          {subtitle !== undefined && <p className="text-[12px] text-muted">{subtitle}</p>}
        </div>
        <Button size="sm" variant="ghost" onClick={() => setAsTable((value) => !value)}>
          {asTable ? "График" : "Таблицей"}
        </Button>
      </figcaption>

      {geometry === null ? (
        <p className="py-6 text-center text-[13px] text-faint">Нет данных за выбранный период</p>
      ) : emptyText !== undefined && points.every((point) => point.value === 0) && !asTable ? (
        <p className="py-6 text-center text-[13px] text-faint">{emptyText}</p>
      ) : asTable ? (
        <div className="max-h-64 overflow-y-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-line">
                <th scope="col" className="py-1.5 text-left text-[11px] font-bold text-faint uppercase">
                  Период
                </th>
                <th scope="col" className="py-1.5 text-right text-[11px] font-bold text-faint uppercase">
                  Значение
                </th>
              </tr>
            </thead>
            <tbody>
              {points.map((point) => (
                <tr key={point.label} className="border-b border-line last:border-0">
                  <td className="py-1.5 text-fg">{point.label}</td>
                  <td className="py-1.5 text-right text-fg tabular-nums">
                    {point.value}
                    {unit}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div ref={box} className="relative">
          <svg
            viewBox={`0 0 ${String(width)} ${String(height)}`}
            className="w-full"
            role="img"
            aria-label={`${title}: от ${points[0]?.label ?? ""} до ${points.at(-1)?.label ?? ""}`}
            onMouseLeave={() => setHover(null)}
          >
            {/* Сетка рецессивна: данные впереди, разметка позади. */}
            {[0, 0.5, 1].map((fraction) => {
              const value = geometry.ceiling * (1 - fraction);
              const y = PAD.top + geometry.plotH * fraction;
              return (
                <g key={fraction}>
                  <line
                    x1={PAD.left}
                    x2={width - PAD.right}
                    y1={y}
                    y2={y}
                    stroke="var(--viz-grid)"
                    strokeWidth={1}
                  />
                  <text
                    x={PAD.left - 6}
                    y={y + 3}
                    textAnchor="end"
                    className="fill-[var(--color-faint)] text-[11px] tabular-nums"
                  >
                    {Math.round(value)}
                  </text>
                </g>
              );
            })}

            <polygon points={geometry.area} fill="var(--series-1)" fillOpacity={0.14} />
            <polyline
              points={geometry.line}
              fill="none"
              stroke="var(--series-1)"
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
            />

            {points.map((point, index) => {
              const cx = geometry.x(index);
              const cy = geometry.y(point.value);
              const emphasised = index === geometry.maxIndex || index === points.length - 1;
              return (
                <g key={point.label}>
                  {/* Цель наведения — вся высота полосы, а не точка в 8px. */}
                  <rect
                    x={cx - (width - PAD.left - PAD.right) / (points.length * 2)}
                    y={PAD.top}
                    width={(width - PAD.left - PAD.right) / points.length}
                    height={geometry.plotH}
                    fill="transparent"
                    onMouseEnter={() => setHover(index)}
                    // Касание: наведения нет, точку выбирают нажатием.
                    onClick={() => setHover(index)}
                  />
                  {hover === index && (
                    <line
                      x1={cx}
                      x2={cx}
                      y1={PAD.top}
                      y2={PAD.top + geometry.plotH}
                      stroke="var(--viz-axis)"
                      strokeWidth={1}
                    />
                  )}
                  {(emphasised || hover === index) && (
                    <>
                      <circle cx={cx} cy={cy} r={4.5} fill="var(--series-1)" stroke="var(--viz-surface)" strokeWidth={2} />
                      <text
                        x={cx}
                        y={cy - 9}
                        textAnchor="middle"
                        className="fill-[var(--color-fg)] text-[12px] font-bold tabular-nums"
                      >
                        {point.value}
                      </text>
                    </>
                  )}
                </g>
              );
            })}

            {points.map((point, index) => {
              // Подписи оси прореживаются по ширине: на узком экране они наезжают.
              const fit = Math.max(2, Math.floor((width - PAD.left - PAD.right) / LABEL_SPACE));
              const step = Math.ceil(points.length / fit);
              const last = index === points.length - 1;
              if (index % step !== 0 && !last) return null;
              // Предпоследняя выбранная подпись, слишком близкая к последней, уступает ей.
              if (!last && points.length - 1 - index < step / 2) return null;
              return (
                <text
                  key={point.label}
                  x={geometry.x(index)}
                  y={height - 8}
                  // Последняя подпись прижата к правому краю: по центру
                  // точки она вылезала за область графика.
                  textAnchor={last && points.length > 1 ? "end" : "middle"}
                  className="fill-[var(--color-faint)] text-[11px]"
                >
                  {point.label}
                </text>
              );
            })}
          </svg>

          {hover !== null && points[hover] !== undefined && (
            <p className="pointer-events-none absolute top-1 right-1 rounded-sm border border-line bg-raised px-2 py-1 text-[11px] shadow-sm">
              <span className="text-muted">{points[hover].label}: </span>
              <span className="font-bold text-fg tabular-nums">
                {points[hover].value}
                {unit}
              </span>
            </p>
          )}
        </div>
      )}
    </figure>
  );
}
